/*
 * Project: Dynamedics Portal Web
 * -----
 * Last Modified: Wednesday November 11th 2021 9:52:07 am
 * Modified By: Senath Sadeesha at <ssweerasinghe@mitrai.com>
 * -----
 * Copyright (c) 2021 Mitra Sparks
 * -----
 * HISTORY:
 * 2022-05-19	NRB	Added access token property 'client_name' to user state property.
 * 2021-11-02 SS changed endpoint for GET user
 */
import jwtDecode from 'jwt-decode';
import { createSlice } from '@reduxjs/toolkit';
import { getAccessTokenDecoded } from '../../utils/functions';
// utils
import axios from '../../utils/axios';
import storage from 'redux-persist/lib/storage';
import { setDashboardDateOption } from './report';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  isAuthenticated: false,
  user: {
    name: '',
    displayName: '',
    firstName: '',
    lastName: '',
    email: '',
    clientName: '',
    roles: []
  }
};

const slice = createSlice({
  name: 'authJwt',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    hasError(state, action) {
      state.error = action.payload;
    },

    // INITIALISE
    getInitialize(state, action) {
      state.isLoading = false;
      state.isAuthenticated = action.payload.isAuthenticated;
      if (action.payload.user) {
        state.user = action.payload.user;
      }
    },

    // LOGIN
    loginSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },

    // REGISTER
    registerSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },

    // LOGOUT
    logoutSuccess(state) {
      state.isAuthenticated = false;
      state.user = null;
    },
    // PASSWORD RESET
    passwordResetSuccess(state) {
      state.isLoading = false;
    },

    setTempPassword(state, action) {
      state.user = { ...state.user, tempPassword: true, ...action.payload };
    },
    resetLoggedNow(state) {
      state.loggedNow = false;
    }
  }
});
//Actions
export const { setTempPassword, resetLoggedNow } = slice.actions;
// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }
  const decoded = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ----------------------------------------------------------------------

export function login({ email, password }) {
  return async (dispatch) => {
    const response = await axios.post('/user/login', {
      username: email,
      password
    });
    const { accessToken, user } = (response.data || {}).data;
    setSession(accessToken);
    const { client_name } = getAccessTokenDecoded(accessToken);
    dispatch(
      slice.actions.loginSuccess({ user: { ...user, clientName: client_name } })
    );
  };
}

// ----------------------------------------------------------------------

export function register({ email, password, firstName, lastName }) {
  return async (dispatch) => {
    const response = await axios.post('/user/register', {
      email,
      password,
      firstName,
      lastName
    });
    const { accessToken, user } = response.data;
    window.localStorage.setItem('accessToken', accessToken);
    const { client_name } = getAccessTokenDecoded(accessToken);
    dispatch(
      slice.actions.registerSuccess({
        user: { ...user, clientName: client_name }
      })
    );
  };
}

// ----------------------------------------------------------------------

export function logout() {
  return async (dispatch) => {
    setSession(null);
    storage.removeItem('persist:dataSets');
    dispatch(slice.actions.logoutSuccess());
    dispatch(setDashboardDateOption('1y'));
  };
}

// ----------------------------------------------------------------------

export function resetPassword({ email, password, newPassword }) {
  return async (dispatch) => {
    try {
      await axios.put('/user/reset-password', {
        username: email,
        password,
        newPassword
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
}

export function changePassword({ email, password, newPassword }) {
  return async (dispatch) => {
    try {
      const response = await axios.put(`/user/reset-password`, {
        username: email,
        password: password,
        newPassword: newPassword
      });
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getInitialize() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const accessToken = window.localStorage.getItem('accessToken');

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const response = await axios.get('/user/info');
        const { client_name } = getAccessTokenDecoded(accessToken);
        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: true,
            user: { ...response.data.data, clientName: client_name }
          })
        );
      } else {
        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: false,
            user: null
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(
        slice.actions.getInitialize({
          isAuthenticated: false,
          user: null
        })
      );
    }
  };
}
